/**
 * OrientacionBookingLauncher — punto de entrada liviano de la reserva de la
 * Orientación gratuita para "Reservar cita" (Navbar y Contacto).
 * CEC-260928-7b98.
 *
 * `OrientacionBookingModal` (y con él el `BookingWizard`) se descarga recién
 * cuando se pulsa "Reservar cita" por primera vez: así no pesa en el paquete
 * principal de todas las páginas. Mientras se descarga se ve el mismo
 * "Cargando…" que mientras llega la terapia. Si la descarga falla, se cierra
 * y lleva a `/terapias`, igual que si faltara la terapia.
 */
import { lazy, Suspense, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Routes } from '../../lib/routes';
import { OrientacionLoading } from './OrientacionLoading';
import type { OrientacionBookingModalProps } from './OrientacionBookingModal';

/** Si no se pudo descargar el código de la reserva: respaldo a /terapias. */
function FallbackATerapias({ open, onClose }: OrientacionBookingModalProps) {
  const navigate = useNavigate();
  useEffect(() => {
    if (!open) return;
    onClose();
    navigate(Routes.therapies);
  }, [open, onClose, navigate]);
  return null;
}

const OrientacionBookingModal = lazy(() =>
  import('./OrientacionBookingModal')
    .then((m) => ({ default: m.OrientacionBookingModal }))
    .catch(() => ({ default: FallbackATerapias })),
);

export function OrientacionBookingLauncher({ open, onClose }: OrientacionBookingModalProps) {
  // Nada se descarga hasta la primera apertura; después queda montado para
  // que el wizard haga su animación de salida.
  const [hasOpened, setHasOpened] = useState(open);
  useEffect(() => {
    if (open) setHasOpened(true);
  }, [open]);

  if (!hasOpened && !open) return null;
  return (
    <Suspense fallback={open ? <OrientacionLoading onClose={onClose} /> : null}>
      <OrientacionBookingModal open={open} onClose={onClose} />
    </Suspense>
  );
}
